import type { ExecutionMode, RemoteExecutionTarget } from '@/components/worktree-controls';
import { placeNewConversation as placeConversationOnCloud } from '@/lib/cloud-link';
import { failureReason } from '@/lib/conversations/precondition-failure';
import { resolveAgentForRuntime, resolvePersistedAgent } from '@/lib/desktop-shell/cloud-agents';
import type { CloudHandoffSource } from '@/lib/desktop-shell/cloud-handoff';
import type { ConversationScope, ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import {
  selectAssociatedWorktreePath,
  selectWorktreeSourceRoot,
} from '@/lib/desktop-shell/conversation-worktree';
import { loadExecutionTargets as loadRemoteExecutionTargets } from '@/lib/desktop-shell/execution-targets';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { localWorkspaceAssociation } from '@/lib/desktop-shell/workspace-replicas';
import { WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { errorMessage } from '@/lib/error-message';
import { type NativeGitStatus, readNativeGitStatus } from '@/lib/native-desktop';
import { sourceControlRepositoryFullName } from '@/lib/sidebar-threads';
import type { SourceControlRepository } from '@/lib/source-control-connector';

/** The primary checkout when the active workspace is a linked Git worktree. */
export function primaryCheckoutPath(gitStatus: NativeGitStatus | null): string | null {
  if (gitStatus === null) return null;
  const currentIndex = gitStatus.worktrees.findIndex((worktree) => worktree.isCurrent);
  if (currentIndex <= 0) return null;
  const primary = gitStatus.worktrees[0];
  return primary === undefined || primary.bare === true ? null : primary.path;
}

/** Whether the active workspace is a linked checkout, including bare-main repositories. */
function isLinkedCheckout(gitStatus: NativeGitStatus | null): boolean {
  return (gitStatus?.worktrees.findIndex((worktree) => worktree.isCurrent) ?? -1) > 0;
}

/**
 * The runtime a conversation is currently executing on. Cloud wins whenever a
 * cloud agent/workspace is selected. Otherwise both Crowdex-managed and
 * externally-created linked checkouts report `worktree`; `local` is reserved
 * for the repository's primary checkout.
 */
export function runtimeMode(input: {
  cloudAgentSelected: boolean;
  executionMode: ExecutionMode;
  gitStatus: NativeGitStatus | null;
}): ExecutionMode {
  if (input.cloudAgentSelected) return 'cloud';
  if (input.executionMode === 'worktree' || isLinkedCheckout(input.gitStatus)) {
    return 'worktree';
  }
  return 'local';
}

export type ExecutionModeControls = {
  currentRuntimeMode: () => ExecutionMode;
  changeExecutionMode: (
    mode: ExecutionMode,
    options?: { allowExistingMessages?: boolean },
  ) => Promise<void>;
  requestContinueLocally: (after?: ExecutionMode) => void;
  continueOn: (destination: ExecutionMode, target?: RemoteExecutionTarget) => Promise<void>;
};

/**
 * Execution-mode routing for the desktop shell: switching between local /
 * worktree / cloud before a conversation starts (`changeExecutionMode`) and
 * handing an in-progress conversation off between runtimes (`continueOn`).
 * Store state is read lazily; the cloud handoff dialog fields, the pending
 * "continue locally" ref, and the sibling handoff closures are injected.
 */
export function createExecutionModeControls({
  store,
  conversations,
  preview,
  getMessagesLength,
  pendingExecutionAfterLocalRef,
  cloudHandoffRepository,
  cloudHandoffSource,
  setCloudHandoffSource,
  loadCloudHandoffOptions,
  requestContinueInCloud,
  handoffRemoteProvider,
  handoffWorktree,
  handoffExternalWorktree,
  prepareManagedWorktree,
  chooseWorkspaceForHandoff,
  placeNewConversation = placeConversationOnCloud,
  loadExecutionTargets = loadRemoteExecutionTargets,
}: {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  preview: boolean;
  getMessagesLength: () => number;
  pendingExecutionAfterLocalRef: { current: ExecutionMode | null };
  cloudHandoffRepository: SourceControlRepository | null;
  cloudHandoffSource: CloudHandoffSource;
  setCloudHandoffSource: (source: CloudHandoffSource) => void;
  loadCloudHandoffOptions: () => Promise<void>;
  requestContinueInCloud: (selected: { id: string }) => Promise<void>;
  handoffRemoteProvider: (target: RemoteExecutionTarget) => Promise<void>;
  handoffWorktree: (destination: 'local' | 'worktree') => Promise<void>;
  handoffExternalWorktree: (input: {
    workspace: string;
    branch: string | null;
  }) => Promise<boolean>;
  prepareManagedWorktree: (sourceRoot?: string | null) => Promise<boolean>;
  chooseWorkspaceForHandoff: (input: {
    logicalWorkspaceId: string;
    name?: string;
  }) => Promise<string | null>;
  /** Starts a conversation without messages on a server of the session (null: this one). */
  placeNewConversation?: (conversationId: string, cloud: boolean) => Promise<void>;
  loadExecutionTargets?: () => Promise<RemoteExecutionTarget[]>;
}): ExecutionModeControls {
  function currentRuntimeMode(): ExecutionMode {
    const { executionMode, gitStatus } = store.getState();
    return runtimeMode({
      cloudAgentSelected: executionMode === 'cloud',
      executionMode,
      gitStatus,
    });
  }

  /**
   * Chooses where a conversation without messages runs: a remote host (on this server or on
   * Crowdex Cloud), or none. A host on the cloud places the conversation there.
   */
  async function placeEmptyConversation(
    conversation: ConversationScope,
    next: RemoteExecutionTarget | null,
  ): Promise<void> {
    const { remoteExecutionTarget: previous } = store.getState();
    const cloud = next?.cloud === true;
    if (cloud !== (previous?.cloud ?? false))
      await whilePlacing(conversation, () => placeNewConversation(conversation.chatId, cloud));
    conversation.whileShown((state) => state.setRemoteExecutionTarget(next));
  }

  /** Nothing is sent while the conversation moves to its host: it would reach the wrong one. */
  async function whilePlacing<T>(
    conversation: ConversationScope,
    operation: () => Promise<T>,
  ): Promise<T> {
    conversation.update({ placementPending: true });
    try {
      return await operation();
    } finally {
      conversation.update({ placementPending: false });
    }
  }

  /**
   * Where the conversation runs before it starts. Each step may wait (a host to place it on, a
   * folder to choose, git): what it settles is the conversation's it started in, which the shell
   * shows only while it is still the one shown (`ConversationScope`).
   */
  async function changeExecutionMode(
    mode: ExecutionMode,
    options: { allowExistingMessages?: boolean } = {},
  ): Promise<void> {
    const conversation = conversations.shown();
    const shownState = store.getState();
    const { executionMode, agents, agentId, gitStatus, syncedWorkspaces, workspace } = shownState;
    const worktreeSourceRoot = selectWorktreeSourceRoot(shownState);
    const messageCount = getMessagesLength();
    if (
      mode === currentRuntimeMode() ||
      (!options.allowExistingMessages && messageCount > 0) ||
      preview
    )
      return;
    const fail = (message: string) =>
      conversation.whileShown((state) => state.setBranchError(message));
    const agent = resolvePersistedAgent(agents, agentId) ?? null;
    const cloudAgentSelected = executionMode === 'cloud';
    if (mode === 'cloud') {
      const cloudTarget = resolveAgentForRuntime(agents, agent?.command, 'cloud');
      if (cloudTarget === undefined) {
        fail('No cloud agent is configured.');
        return;
      }
      if (messageCount === 0) {
        // Without a chosen host: the first remote host, this server's then the cloud's.
        const host =
          store.getState().remoteExecutionTarget ??
          (await whilePlacing(conversation, loadExecutionTargets))[0] ??
          null;
        if (host === null) {
          fail(
            'Sign in to Crowdex Cloud in Settings → Account to send conversations to the cloud.',
          );
          return;
        }
        await placeEmptyConversation(conversation, host);
      }
      conversation.place({
        executionMode: 'cloud',
        ...(cloudTarget.id === agentId ? {} : { agentId: cloudTarget.id }),
      });
      if (!conversation.shown()) return;
      const remoteBranch = cloudHandoffRepository?.defaultBranch ?? gitStatus?.branch ?? null;
      if (
        cloudHandoffSource.type === 'workspace' &&
        remoteBranch !== null &&
        remoteBranch.length > 0
      ) {
        setCloudHandoffSource({ type: 'remote', branch: remoteBranch });
      }
      void loadCloudHandoffOptions().catch(() => undefined);
      return;
    }
    if (messageCount === 0) await placeEmptyConversation(conversation, null);
    if (cloudAgentSelected) {
      const localTarget = resolveAgentForRuntime(agents, agent?.command, 'local');
      let localAssociation = localWorkspaceAssociation(workspace, syncedWorkspaces);
      if (localTarget === undefined) {
        fail('No local agent is available.');
        return;
      }
      const agentChange = localTarget.id === agentId ? {} : { agentId: localTarget.id };
      // A conversation without messages carries nothing back: it returns to the last folder,
      // but for a forge repository the cloud alone has, which gets a folder of this computer.
      const remembered = localStorage.getItem(WORKSPACE_KEY);
      const forgeRepository = sourceControlRepositoryFullName(workspace);
      if (
        localAssociation === undefined &&
        messageCount === 0 &&
        remembered !== null &&
        forgeRepository === null
      ) {
        conversation.place({ ...agentChange, workspace: remembered, executionMode: mode });
        const status = await readNativeGitStatus(remembered).catch(() => null);
        conversation.whileShown((state) => state.setGitStatus(status));
        if (mode === 'local') return;
      } else {
        if (localAssociation === undefined) {
          // A conversation's own checkout of a repository is its project's.
          const source =
            syncedWorkspaces.find((entry) => entry.rootPath === workspace) ??
            syncedWorkspaces.find(
              (entry) =>
                entry.logicalWorkspaceId !== undefined &&
                forgeRepository !== null &&
                sourceControlRepositoryFullName(entry.rootPath) === forgeRepository,
            );
          if (source?.logicalWorkspaceId === undefined) {
            fail('This cloud conversation has no project identity.');
            return;
          }
          const selectedPath = await chooseWorkspaceForHandoff({
            logicalWorkspaceId: source.logicalWorkspaceId,
            name: source.name,
          });
          if (selectedPath === null) return;
          localAssociation = store
            .getState()
            .syncedWorkspaces.find((entry) => entry.rootPath === selectedPath);
        }
        const localWorkspace = localAssociation?.rootPath;
        if (localWorkspace === undefined) {
          fail('The selected local workspace could not be saved.');
          return;
        }
        let restoredStatus: NativeGitStatus | null;
        try {
          restoredStatus = await readNativeGitStatus(localWorkspace);
        } catch (error) {
          // A plain folder runs locally as it is; only a worktree needs a repository, and a
          // folder that is gone or unreadable says so in its own words.
          const reason = failureReason(error);
          if (reason !== 'not_a_repository' || mode !== 'local') {
            fail(
              reason === 'not_a_repository'
                ? `${localWorkspace} is not a Git repository: a worktree needs one. Run the conversation locally instead.`
                : errorMessage(error),
            );
            return;
          }
          restoredStatus = null;
        }
        const restoredPrimaryCheckout = primaryCheckoutPath(restoredStatus);
        if (
          mode === 'local' &&
          isLinkedCheckout(restoredStatus) &&
          restoredPrimaryCheckout === null
        ) {
          fail('This repository has no primary working checkout.');
          return;
        }
        const resolvedWorkspace =
          mode === 'local' ? (restoredPrimaryCheckout ?? localWorkspace) : localWorkspace;
        const resolvedStatus =
          resolvedWorkspace === localWorkspace
            ? restoredStatus
            : await readNativeGitStatus(resolvedWorkspace).catch(() => null);
        conversation.place({ ...agentChange, workspace: resolvedWorkspace, executionMode: mode });
        conversation.whileShown((state) => state.setGitStatus(resolvedStatus));
        if (mode === 'local') return;
      }
    }
    if (mode === 'local') {
      const primaryCheckout = worktreeSourceRoot ?? primaryCheckoutPath(gitStatus);
      if (primaryCheckout === null && isLinkedCheckout(gitStatus)) {
        fail('This repository has no primary working checkout.');
        return;
      }
      if (primaryCheckout !== null && workspace !== primaryCheckout) {
        conversation.place({ workspace: primaryCheckout, executionMode: 'local' });
        const status = await readNativeGitStatus(primaryCheckout).catch(() => null);
        conversation.whileShown((state) => state.setGitStatus(status));
      } else {
        conversation.place({ executionMode: 'local' });
      }
      return;
    }
    conversation.place({ executionMode: 'worktree' });
  }

  function requestContinueLocally(after: ExecutionMode = 'local'): void {
    const { agents, agentId, setAgentHandoffError, setLocalHandoffAgentId, setLocalHandoffOpen } =
      store.getState();
    const agent = resolvePersistedAgent(agents, agentId) ?? null;
    const localTarget = resolveAgentForRuntime(agents, agent?.command, 'local');
    if (localTarget === undefined) {
      setAgentHandoffError('No local agent is available.');
      return;
    }
    pendingExecutionAfterLocalRef.current = after;
    setLocalHandoffAgentId(localTarget.id);
    setAgentHandoffError(null);
    setLocalHandoffOpen(true);
  }

  async function continueOn(
    destination: ExecutionMode,
    target?: RemoteExecutionTarget,
  ): Promise<void> {
    if (preview) return;
    const conversation = conversations.shown();
    if (getMessagesLength() === 0) {
      // A new conversation starts on the server that runs the chosen target.
      if (destination === 'cloud' && target !== undefined)
        await placeEmptyConversation(conversation, target);
      // Another conversation shown meanwhile: the choice was this one's, not that one's.
      if (!conversation.shown()) return;
      if (destination !== currentRuntimeMode()) await changeExecutionMode(destination);
      return;
    }
    const { setRemoteExecutionTarget } = store.getState();
    if (destination === 'cloud' && target !== undefined) setRemoteExecutionTarget(target);
    if (destination !== 'cloud') setRemoteExecutionTarget(null);
    const current = currentRuntimeMode();
    if (destination === 'cloud' && current === 'cloud' && target !== undefined) {
      await handoffRemoteProvider(target);
      return;
    }
    if (destination === current) return;
    // Its worktree as the server records it: the same on any device, after any reload.
    const shownState = store.getState();
    const { chatId, gitStatus } = shownState;
    const associatedWorktreePath = selectAssociatedWorktreePath(shownState);
    const worktreeSourceRoot = selectWorktreeSourceRoot(shownState);
    if (destination === 'cloud') {
      await requestContinueInCloud({ id: chatId });
      return;
    }
    if (current === 'cloud') {
      requestContinueLocally(destination);
      return;
    }
    if (destination === 'local') {
      if (associatedWorktreePath !== null) {
        await handoffWorktree('local');
        return;
      }
      const primaryCheckout = worktreeSourceRoot ?? primaryCheckoutPath(gitStatus);
      if (primaryCheckout !== null) {
        localStorage.setItem(WORKSPACE_KEY, primaryCheckout);
        const primaryStatus = await readNativeGitStatus(primaryCheckout).catch(() => null);
        if (!conversation.shown()) return;
        const handedOff = await handoffExternalWorktree({
          workspace: primaryCheckout,
          branch: primaryStatus?.branch ?? null,
        });
        if (handedOff) conversation.whileShown((state) => state.setGitStatus(primaryStatus));
      } else if (isLinkedCheckout(gitStatus)) {
        store.getState().setBranchError('This repository has no primary working checkout.');
      }
      return;
    }
    if (associatedWorktreePath !== null) {
      await handoffWorktree('worktree');
      return;
    }
    conversation.place({ executionMode: 'worktree' });
    await prepareManagedWorktree();
  }

  return { currentRuntimeMode, changeExecutionMode, requestContinueLocally, continueOn };
}
